import { AttachmentCategoryEnum, ModelsEnum } from "@nlc/shared-types";
import { Link2, Plus, UploadCloud } from "lucide-react";
import { useCallback, useRef, useState } from "react";

import type { ManagedFile } from "@/components/file-uploader/types";

import { Button } from "@/components/button";
// Deep imports, not the package barrel: the barrel also exports the file
// lists, which pull the PDF viewer into this route's bundle.
import { FileUploadDropZone } from "@/components/file-uploader/components/FileUploadDropZone";
import { FileUploadTrigger } from "@/components/file-uploader/components/FileUploadTrigger";
import { ProgressSpinner } from "@/components/file-uploader/components/ProgressSpinner";
import { FileUploaderProvider } from "@/components/file-uploader/FileUploaderContext";
import { useFileUploaderContext } from "@/components/file-uploader/UseFileUploaderContext";
import { toast } from "@/components/toast/toast-queue";

import type { UploadPromptData } from "../../types/mbc-chat.types";
import type {
  AssetFileRef,
  AssetGroupChange,
  AssetLinkRef,
} from "../upload-assets/types";

import { patchBrief } from "../../lib/brief-api";
import { requestIdFromSessionId } from "../../lib/session-ids";

/** What the part needs to write the brief. Threaded down from
 * `MBCConversation`, which already owns the session ids. */
export type MbcUploadBinding = {
  /** Refetch the brief so the sidebar catches up with what was just added. */
  onUploaded: () => void;
  resourceId?: string;
  threadId?: string;
};

type UploadPromptPartProps = {
  isDisabled: boolean;
  onSelectChoice: (label: string) => void;
  prompt: UploadPromptData;
  upload?: MbcUploadBinding;
};

const LEAD_TEXT = {
  assets: "Share the files we should use in the designs.",
  inspirations: "Share files or links to use as inspiration.",
} as const;

/**
 * Progress/outcome for the files this block is uploading. The package's own
 * `FileUploadList` would do this, but it also offers preview, download and
 * delete — none of which apply mid-conversation, and its preview modal is what
 * drags the PDF viewer into the bundle. Removal arrives with the delete route.
 */
function UploadRows() {
  const { files } = useFileUploaderContext();

  if (files.length === 0) return null;

  return (
    <ul className="mt-md flex flex-col gap-xs">
      {files.map((file) => (
        <li
          className="flex items-center justify-between gap-sm rounded-lg bg-bg-secondary px-md py-sm text-sm text-text-primary"
          key={file.id}
        >
          <span className="truncate">{file.file?.name ?? file.name}</span>
          {file.status === "error" ? (
            <span className="shrink-0 text-xs text-text-error-primary">
              {file.errorMessage ?? "Upload failed"}
            </span>
          ) : null}
          {file.status === "uploading" || file.status === "pending" ? (
            <ProgressSpinner progress={file.progress} size={16} />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function toAssetFile(file: ManagedFile): AssetFileRef {
  return {
    attachmentId: file.attachmentId as number,
    mime: file.file?.type || file.mime || null,
    name: file.file?.name ?? file.name ?? "file",
    size: file.file?.size ?? null,
    url: file.url as string,
  };
}

/**
 * The in-chat upload block (Q4/Q5), rendered from the durable
 * `data-uploadPrompt` part the agent's `showUploadPrompt` tool writes.
 *
 * Adds to the brief's `assets` / `inspirations` group, the same field
 * `UploadAssetsModal` edits — the two surfaces are alternative entry points to
 * one field, not two stores.
 */
export function UploadPromptPart({
  isDisabled,
  onSelectChoice,
  prompt,
  upload,
}: UploadPromptPartProps) {
  const [links, setLinks] = useState<AssetLinkRef[]>([]);
  const [linkInput, setLinkInput] = useState("");
  const [uploadedCount, setUploadedCount] = useState(0);
  // Attachments already sent, so a re-fired `onFilesChange` doesn't resend them.
  const sentFileIdsRef = useRef(new Set<number>());

  const requestId = upload?.threadId
    ? requestIdFromSessionId(upload.threadId)
    : null;

  const persist = useCallback(
    (change: AssetGroupChange) => {
      if (!upload?.threadId) return;

      patchBrief(upload.threadId, upload.resourceId, {
        assetChanges: [{ target: prompt.target, ...change }],
      })
        .then(upload.onUploaded)
        .catch(() => {
          toast.add({
            description: "It wasn't saved to your brief. Please try again.",
            title: "Couldn't add that to the brief",
            type: "error",
          });
        });
    },
    [prompt.target, upload],
  );

  const handleFilesChange = useCallback(
    (files: ManagedFile[]) => {
      const uploaded = files.filter(
        (file) =>
          file.status === "success" &&
          typeof file.attachmentId === "number" &&
          typeof file.url === "string",
      );

      setUploadedCount(uploaded.length);

      const fresh = uploaded.filter(
        (file) => !sentFileIdsRef.current.has(file.attachmentId as number),
      );

      if (fresh.length > 0) {
        for (const file of fresh) {
          sentFileIdsRef.current.add(file.attachmentId as number);
        }
        persist({ addFiles: fresh.map((file) => toAssetFile(file)) });
      }
    },
    [persist],
  );

  const addLink = () => {
    const url = linkInput.trim();

    if (!url || links.some((link) => link.url === url)) return;

    setLinks((previous) => [...previous, { url }]);
    setLinkInput("");
    persist({ addLinks: [url] });
  };

  // No request means no `attachments` row to hang an upload on. The block only
  // renders after a turn completed, so this is a guard, not a real state.
  if (requestId === null) return null;

  const total = uploadedCount + links.length;

  return (
    <div className="mt-md flex flex-col gap-lg">
      <FileUploaderProvider
        category={
          prompt.target === "inspirations"
            ? AttachmentCategoryEnum.EXAMPLE
            : AttachmentCategoryEnum.ASSET
        }
        isMbc
        onFilesChange={handleFilesChange}
        parentId={requestId}
        parentType={ModelsEnum.REQUEST}
      >
        <FileUploadDropZone
          className={({ isDropTarget }) =>
            `flex flex-col items-center gap-lg rounded-xl border px-3xl py-xl transition-colors ${
              isDropTarget
                ? "border-2 border-border-brand bg-bg-brand-secondary"
                : "border-border-secondary bg-bg-primary hover:bg-bg-primary-hover"
            }`
          }
        >
          <FileUploadTrigger>
            <Button
              className="flex w-full flex-col items-center gap-lg outline-hidden"
              isDisabled={isDisabled}
              variant="unstyled"
            >
              <span className="grid size-10 place-items-center rounded-md border border-border-secondary">
                <UploadCloud className="size-5 text-fg-secondary" />
              </span>
              <span className="flex flex-col gap-xs text-center">
                <span className="text-sm font-semibold text-button-tertiary-color-fg">
                  Click to upload{" "}
                  <span className="font-normal text-text-tertiary">
                    or drag and drop
                  </span>
                </span>
                <span className="text-xs text-text-tertiary">
                  {LEAD_TEXT[prompt.target]} When sharing links please grant
                  access to{" "}
                  <span className="text-fg-brand-primary">
                    admin@nolimitcreatives.com
                  </span>{" "}
                  for Google Drive, and{" "}
                  <span className="text-fg-brand-primary">teams@nlc.com</span>{" "}
                  for Dropbox.
                </span>
              </span>
            </Button>
          </FileUploadTrigger>
        </FileUploadDropZone>

        <UploadRows />
      </FileUploaderProvider>

      <div className="flex items-end gap-md">
        <label className="flex flex-1 flex-col gap-sm">
          <span className="text-sm font-medium text-text-primary">
            Add links
          </span>
          <input
            className="h-11 rounded-lg border border-border-primary bg-bg-primary px-md text-sm text-text-primary shadow-xs outline-none placeholder:text-text-placeholder disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isDisabled}
            onChange={(event) => setLinkInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addLink();
              }
            }}
            placeholder="Add link here"
            value={linkInput}
          />
        </label>
        <Button
          aria-label="Add link"
          className="size-11 shrink-0"
          isDisabled={isDisabled}
          onPress={addLink}
          variant="secondary"
        >
          <Plus className="size-5" />
        </Button>
      </div>

      {links.length > 0 ? (
        <ul className="flex flex-col gap-xs">
          {links.map((link) => (
            <li
              className="flex items-center gap-sm rounded-lg bg-bg-secondary px-md py-sm text-sm text-text-primary"
              key={link.url}
            >
              <Link2 className="size-4 shrink-0 text-text-tertiary" />
              <span className="truncate">{link.url}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex gap-lg">
        <Button
          isDisabled={isDisabled || total === 0}
          onPress={() => onSelectChoice("Done!")}
          variant="primary"
        >
          Done!
        </Button>
        <Button
          isDisabled={isDisabled}
          onPress={() => onSelectChoice("Skip this step")}
          variant="secondary"
        >
          Skip this step
        </Button>
      </div>
    </div>
  );
}
